import { FunctionComponent, ReactNode, isValidElement, useMemo } from "react";
import { Switch } from "@headlessui/react";
import { cn } from "@helloaudio/utils";

interface SingleLabelProps {
  description?: ReactNode;
  disabled?: boolean;
  label?: ReactNode;
  labelClassName?: string;
  wrapperClassName?: string;
}

interface ToggleLabel {
  left: ReactNode;
  right: ReactNode;
}

interface ToggleProps {
  checked?: boolean;
  checkedClassName?: string;
  className?: string;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
  label?: ReactNode | ToggleLabel;
  labelClassName?: string;
  onChange: (value: boolean) => void;
  switchPosition?: string;
  uncheckedClassName?: string;
  value: boolean;
}

const isValidReactNode = (node: ReactNode | ToggleLabel): boolean => {
  return typeof node === "string" || typeof node === "number" || isValidElement(node);
};

const SingleLabel: FunctionComponent<SingleLabelProps> = ({
  description,
  disabled,
  label,
  labelClassName,
  wrapperClassName,
}) => {
  return (
    <Switch.Label
      as="span"
      className={cn(
        "tw-text-base",
        wrapperClassName,
        disabled ? "tw-cursor-default" : "tw-cursor-pointer",
      )}
    >
      {(typeof label === "string" || typeof label === "number" || isValidElement(label)) && (
        <span className={cn("tw-font-semibold tw-text-black", labelClassName)}>{label}</span>
      )}

      {description && (
        <span className={cn("tw-text-gray-500", label && "tw-ml-2")}>{description}</span>
      )}
    </Switch.Label>
  );
};

const Toggle: FunctionComponent<ToggleProps> = ({
  checkedClassName,
  className,
  description,
  disabled,
  label,
  labelClassName,
  onChange,
  switchPosition = "left",
  uncheckedClassName,
  value,
}) => {
  const switchStyles = [
    "tw-p-0",
    "tw-relative",
    "tw-inline-flex",
    "tw-flex-shrink-0",
    "tw-h-5",
    "tw-w-9",
    "tw-border-2",
    "tw-border-transparent",
    "tw-rounded-full",
    "tw-transition-colors",
    "tw-ease-in-out",
    "tw-duration-200",
    "focus:tw-outline-none",
    "focus:tw-ring-2",
    "focus:tw-ring-offset-0",
    "focus:tw-ring-green-300",
  ];
  const switchStylesDisabled = ["tw-cursor-not-allowed tw-opacity-75"];
  const switchStylesChecked = useMemo(() => {
    return cn(["tw-cursor-pointer, tw-bg-green-500", checkedClassName]);
  }, [checkedClassName]);
  const switchStylesUnchecked = useMemo(() => {
    return cn(["tw-cursor-pointer, tw-bg-gray-200", uncheckedClassName]);
  }, [uncheckedClassName]);
  const hasSingleLabel = useMemo(
    () => isValidReactNode(label) || description,
    [label, description],
  );
  const hasMultipleLabels = useMemo(
    () =>
      !hasSingleLabel &&
      typeof label === "object" &&
      label != null &&
      isValidReactNode((label as ToggleLabel).left) &&
      isValidReactNode((label as ToggleLabel).right),
    [hasSingleLabel, label],
  );

  return (
    <Switch.Group as="div" className={cn("tw-flex tw-items-center", className)}>
      {hasMultipleLabels && (
        <SingleLabel
          label={(label as ToggleLabel).left}
          labelClassName={labelClassName}
          description={description}
          disabled={disabled}
        />
      )}
      {hasSingleLabel && switchPosition === "right" && (
        <SingleLabel
          description={description}
          disabled={disabled}
          label={label as ReactNode}
          labelClassName={labelClassName}
          wrapperClassName="tw-mr-3"
        />
      )}
      <Switch
        checked={!!value}
        onChange={onChange}
        className={cn(
          switchStyles,
          value ? switchStylesChecked : switchStylesUnchecked,
          disabled ? switchStylesDisabled : [],
        )}
        disabled={!!disabled}
      >
        <span
          aria-hidden="true"
          className={cn(
            value ? "tw-translate-x-4" : "tw-translate-x-0",
            "tw-pointer-events-none tw-inline-block tw-h-4 tw-w-4 tw-rounded-full tw-bg-white tw-shadow tw-transform tw-ring-0 tw-transition tw-ease-in-out tw-duration-200",
          )}
        />
      </Switch>
      {hasMultipleLabels && (
        <SingleLabel
          description={description}
          disabled={disabled}
          label={(label as ToggleLabel).right}
          labelClassName={labelClassName}
        />
      )}
      {hasSingleLabel && switchPosition !== "right" && (
        <SingleLabel
          description={description}
          disabled={disabled}
          label={label as ReactNode}
          labelClassName={labelClassName}
          wrapperClassName="tw-ml-3"
        />
      )}
    </Switch.Group>
  );
};

export default Toggle;
